import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { Button } from '../../components/ui/Button'
import { Callout } from '../../components/ui/Callout'
import { CodeInput, isCodeComplete } from '../../components/ui/CodeInput'
import { Field, Input } from '../../components/ui/Field'
import { QrCode } from '../../components/ui/QrCode'
import { StatusBadge, type Tone } from '../../components/ui/StatusBadge'
import {
  ApiError,
  beginMfaEnrolment,
  confirmMfaEnrolment,
  disableMfa,
  fetchMfaStatus,
  regenerateRecoveryCodes,
  type MfaStatus,
} from '../../lib/api'
import { RecoveryCodes } from './RecoveryCodes'

/** How the second-factor state is presented. Never colour alone. */
const MFA_TONE: Record<MfaStatus['status'], { tone: Tone; label: string }> = {
  enabled: { tone: 'success', label: 'On' },
  pending: { tone: 'warning', label: 'Setup unfinished' },
  disabled: { tone: 'neutral', label: 'Off' },
}

/**
 * What the page is doing right now.
 *
 * Enrolment is modelled as an explicit state rather than a pile of booleans
 * because the secret exists in memory for exactly one step: the server shows
 * it once, and a reload loses it. Making that a distinct state is what stops
 * the UI from rendering a QR code for a secret it no longer has.
 */
type Flow =
  | { kind: 'idle' }
  | { kind: 'enrolling'; secret: string; uri: string }
  | { kind: 'codes'; codes: string[]; reason: 'enrolled' | 'regenerated' }

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.problem.detail : fallback
}

/** Groups the base32 secret into fours so it can be typed without losing place. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function SecurityPage() {
  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [flow, setFlow] = useState<Flow>({ kind: 'idle' })

  const refresh = useCallback(async () => {
    try {
      setStatus(await fetchMfaStatus())
      setLoadError(null)
    } catch (err) {
      setLoadError(message(err, 'Could not load your security settings.'))
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (loadError) {
    return (
      <>
        <PageHeader />
        <div className="lg-alert" role="alert">
          {loadError}
        </div>
      </>
    )
  }

  if (!status) {
    return (
      <>
        <PageHeader />
        <p className="lg-muted" role="status">
          Loading…
        </p>
      </>
    )
  }

  // The one-time code display takes over the page. It is the only moment in
  // the product where dismissing the screen destroys something irrecoverable,
  // so nothing else competes for attention while it is up.
  if (flow.kind === 'codes') {
    return (
      <>
        <PageHeader />
        <section className="lg-card">
          <h2>
            {flow.reason === 'enrolled'
              ? 'Two-factor authentication is on'
              : 'New recovery codes'}
          </h2>
          {flow.reason === 'regenerated' && (
            <p className="lg-muted lg-card__lede">Your previous codes no longer work.</p>
          )}
          <RecoveryCodes
            codes={flow.codes}
            onAcknowledge={() => {
              setFlow({ kind: 'idle' })
              void refresh()
            }}
          />
        </section>
      </>
    )
  }

  return (
    <>
      <PageHeader />

      {status.grace_until && <GraceNotice until={status.grace_until} />}

      <div className="lg-stack lg-settings">
        <StatusCard status={status} />

        {status.status === 'enabled' ? (
          <>
            <RegenerateCard
              remaining={status.recovery_codes_remaining}
              onIssued={(codes) => setFlow({ kind: 'codes', codes, reason: 'regenerated' })}
            />
            <DisableCard status={status} onDisabled={refresh} />
          </>
        ) : (
          <EnrolCard
            status={status}
            flow={flow}
            onFlow={setFlow}
            onEnrolled={(codes) => setFlow({ kind: 'codes', codes, reason: 'enrolled' })}
          />
        )}
      </div>
    </>
  )
}

function PageHeader() {
  return (
    <header className="lg-page-header">
      <div>
        <h1>Security</h1>
        <p className="lg-muted lg-card__lede">
          How you prove it is you when you sign in.
        </p>
      </div>
    </header>
  )
}

/**
 * The deadline after an administrator reset.
 *
 * Shown loudly because the consequence is silent: the account signs in
 * normally right up until the window closes, and then simply stops working.
 * Someone who was never told has no way to connect the two events.
 */
function GraceNotice({ until }: { until: string }) {
  return (
    <Callout tone="warning" title="Set up two-factor authentication before the deadline">
      Your second factor was reset by an administrator. Until {formatDate(until)} you can sign in
      with your password alone. After that your organisation&rsquo;s policy applies again and you
      will need a second factor to get in.
    </Callout>
  )
}

function StatusCard({ status }: { status: MfaStatus }) {
  const meta = MFA_TONE[status.status]
  return (
    <section className="lg-card">
      <div className="lg-row">
        <h2>Two-factor authentication</h2>
        <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
      </div>

      <p className="lg-muted lg-card__lede">
        A code from an authenticator app, on top of your password. It is what stops a stolen
        password on its own from being enough.
      </p>

      <dl className="lg-facts">
        {status.enrolled_at && (
          <div className="lg-facts__item">
            <dt>Turned on</dt>
            <dd>{formatDate(status.enrolled_at)}</dd>
          </div>
        )}
        {status.status === 'enabled' && (
          <div className="lg-facts__item">
            <dt>Recovery codes left</dt>
            <dd className="lg-numeric">{status.recovery_codes_remaining}</dd>
          </div>
        )}
        <div className="lg-facts__item">
          <dt>Required by your organisation</dt>
          <dd>{status.required_by_organisation ? 'Yes' : 'No'}</dd>
        </div>
      </dl>
    </section>
  )
}

// --- enrolment ---------------------------------------------------------------

interface EnrolCardProps {
  status: MfaStatus
  flow: Flow
  onFlow: (flow: Flow) => void
  onEnrolled: (codes: string[]) => void
}

function EnrolCard({ status, flow, onFlow, onEnrolled }: EnrolCardProps) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const enrolling = flow.kind === 'enrolling' ? flow : null

  async function start() {
    setBusy(true)
    setError(null)
    try {
      const result = await beginMfaEnrolment()
      onFlow({ kind: 'enrolling', secret: result.secret, uri: result.provisioning_uri })
      setCode('')
    } catch (err) {
      setError(message(err, 'Could not start setup.'))
    } finally {
      setBusy(false)
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await confirmMfaEnrolment(code)
      onEnrolled(result.recovery_codes)
    } catch (err) {
      setError(message(err, 'Could not confirm that code.'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  if (!enrolling) {
    return (
      <section className="lg-card">
        <h2>Set up an authenticator app</h2>
        <p className="lg-muted lg-card__lede">
          {status.status === 'pending'
            ? // The pending secret is on the server, but the plaintext was shown
              // once and is gone. There is nothing to resume, so say so plainly
              // rather than offering a Continue that cannot work.
              'You started setting this up but did not finish. The previous code is no longer shown, so start again.'
            : 'You will scan a QR code with an app like 1Password, Google Authenticator, or Aegis, then enter the code it shows to prove it worked.'}
        </p>
        {error && (
          <div className="lg-alert" role="alert">
            {error}
          </div>
        )}
        <div>
          <Button variant="primary" onClick={start} loading={busy}>
            {status.status === 'pending' ? 'Start again' : 'Set up'}
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className="lg-card">
      <h2>Scan this with your authenticator app</h2>

      <div className="lg-enrol">
        <QrCode
          value={enrolling.uri}
          label="QR code for setting up your authenticator app. If you cannot scan it, use the setup key shown beside it."
        />

        <div className="lg-stack lg-enrol__manual">
          <div>
            <p className="lg-overline">Or enter this key by hand</p>
            {/* Grouped in fours and selectable. Long unbroken base32 is
                mistyped constantly, and the QR is useless on the machine that
                is displaying it. */}
            <p className="lg-secret">{groupSecret(enrolling.secret)}</p>
          </div>

          <form className="lg-stack" onSubmit={confirm} noValidate>
            <Field
              label="Code from your app"
              required
              hint="Six digits. It changes every 30 seconds."
              error={error ?? undefined}
            >
              {(props) => <CodeInput {...props} mode="totp" value={code} onChange={setCode} />}
            </Field>

            <div className="lg-row">
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={!isCodeComplete('totp', code)}
              >
                Turn on
              </Button>
              {/* Cancelling leaves the unproven secret on the server, where it
                  expires on its own and can never authenticate. Nothing needs
                  undoing, so this is a plain state change. */}
              <Button variant="ghost" onClick={() => onFlow({ kind: 'idle' })}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}

// --- recovery codes ----------------------------------------------------------

function RegenerateCard({
  remaining,
  onIssued,
}: {
  remaining: number
  onIssued: (codes: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Below a third of the original ten, running out stops being hypothetical.
  const low = remaining <= 3

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await regenerateRecoveryCodes(code)
      setOpen(false)
      setCode('')
      onIssued(result.recovery_codes)
    } catch (err) {
      setError(message(err, 'Could not issue new codes.'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="lg-card">
      <h2>Recovery codes</h2>
      <p className="lg-muted lg-card__lede">
        Single-use codes that get you in when you do not have your phone. You have{' '}
        <span className="lg-numeric">{remaining}</span> left.
      </p>

      {low && (
        <Callout tone="warning" title="You are running low">
          When these run out and you lose your phone, only an administrator can restore access.
          Generating a new set replaces every code you have now.
        </Callout>
      )}

      {open ? (
        <form className="lg-stack" onSubmit={submit} noValidate>
          <Callout tone="danger" title="Your current codes stop working">
            Every code on your existing list is replaced. Make sure you can save the new ones before
            you continue.
          </Callout>
          <Field
            label="Code from your app"
            required
            hint="Required so a stolen session cannot quietly mint itself a new set."
            error={error ?? undefined}
          >
            {(props) => <CodeInput {...props} mode="totp" value={code} onChange={setCode} />}
          </Field>
          <div className="lg-row">
            <Button
              type="submit"
              variant="primary"
              loading={busy}
              disabled={!isCodeComplete('totp', code)}
            >
              Replace my codes
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false)
                setCode('')
                setError(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button onClick={() => setOpen(true)}>Generate new codes</Button>
        </div>
      )}
    </section>
  )
}

// --- disable -----------------------------------------------------------------

function DisableCard({ status, onDisabled }: { status: MfaStatus; onDisabled: () => void }) {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The server refuses with mfa_required_by_policy. Not offering the form at
  // all is kinder than letting someone type their password into a control
  // that was never going to work.
  if (status.required_by_organisation) {
    return (
      <section className="lg-card">
        <h2>Turning it off</h2>
        <p className="lg-muted lg-card__lede">
          Your organisation requires two-factor authentication, so it cannot be turned off. To move
          it to a new phone, generate new recovery codes first, then ask an administrator to reset
          your second factor.
        </p>
      </section>
    )
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await disableMfa(password, code)
      setOpen(false)
      setPassword('')
      setCode('')
      onDisabled()
    } catch (err) {
      setError(message(err, 'Could not turn off two-factor authentication.'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="lg-card">
      <h2>Turn off two-factor authentication</h2>
      <p className="lg-muted lg-card__lede">
        Your account goes back to being protected by your password alone.
      </p>

      {open ? (
        <form className="lg-stack" onSubmit={submit} noValidate>
          <Callout tone="danger" title="This lowers the protection on your account">
            A leaked or guessed password will be enough to sign in as you. Your recovery codes are
            deleted too.
          </Callout>

          <Field label="Your password" required>
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Code from your app"
            required
            // Both factors, deliberately: knowing the password must not be
            // enough to remove the control that exists because passwords leak.
            hint="Your password alone is not enough to remove your second factor."
            error={error ?? undefined}
          >
            {(props) => <CodeInput {...props} mode="totp" value={code} onChange={setCode} />}
          </Field>

          <div className="lg-row">
            <Button
              type="submit"
              variant="danger"
              loading={busy}
              disabled={password === '' || !isCodeComplete('totp', code)}
            >
              Turn it off
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false)
                setPassword('')
                setCode('')
                setError(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button variant="danger" onClick={() => setOpen(true)}>
            Turn off
          </Button>
        </div>
      )}
    </section>
  )
}
